import { useState } from "react";
import { useTranslation } from "react-i18next";

import { getErrorMessage } from "../../lib/query/errors";
import type { StipulationActionReasonView, StipulationActionView, StipulationView } from "./api/types";
import { useApplyStipulationAction } from "./api/useApplyStipulationAction";
import { usePorExceptionMutation } from "./api/usePorExceptionMutation";
import { useStipulationEdit, useStipulationEditsStore } from "./stipulationEditsStore";

// currentAction values (lower-cased) that mean the stip is done and has no
// pending action to select, so the dropdown trigger should read "Complete"
// instead of the default "Select One" placeholder.
const RESOLVED_CURRENT_ACTIONS = new Set(["closed", "waived", "exception"]);

export interface StipulationRowActions {
  selectedAction?: StipulationActionView;
  selectedReason?: StipulationActionReasonView;
  isResolvedAction: boolean;
  currentLabel?: string;
  note?: string;
  porExceptionChecked: boolean;
  saveError?: string;
  porError?: string;
  isSaving: boolean;
  isPorSaving: boolean;
  handleSelect: (action: StipulationActionView, reason?: StipulationActionReasonView) => void;
  persistAction: (actionCode: string, reasonPk?: number, noteText?: string) => void;
  setNote: (note: string) => void;
  handlePorExceptionChange: (checked: boolean) => void;
}

/**
 * Owns a stipulation row's business logic: staging selections/notes/POR
 * exception in the edits store, persisting them through the BFF mutations,
 * and tracking inline save errors. Keeps `StipulationRow` presentational.
 * @param {string} [approvalId] - The current approval, or undefined pre-load.
 * @param {StipulationView} stip - The row's stipulation.
 * @returns {StipulationRowActions} Derived view state and row handlers.
 */
export function useStipulationRowActions(approvalId: string | undefined, stip: StipulationView): StipulationRowActions {
  const { t } = useTranslation();
  const [saveError, setSaveError] = useState<string>();
  const [porError, setPorError] = useState<string>();
  const edit = useStipulationEdit(stip.id);
  const clearEdit = useStipulationEditsStore((state) => state.clear);
  const setAction = useStipulationEditsStore((state) => state.setAction);
  const setNoteInStore = useStipulationEditsStore((state) => state.setNote);
  const setPorException = useStipulationEditsStore((state) => state.setPorException);
  const applyAction = useApplyStipulationAction(approvalId ?? "");
  const porExceptionMutation = usePorExceptionMutation();

  const selectedAction = edit.actionCode
    ? stip.availableActions.find((action) => action.actionCode === edit.actionCode)
    : undefined;
  const selectedReason = selectedAction?.reasons.find((reason) => reason.reasonPk === edit.reasonPk);
  const isResolvedAction = RESOLVED_CURRENT_ACTIONS.has(stip.currentAction?.toLowerCase() ?? "");
  const currentLabel =
    selectedReason?.displayName ?? selectedAction?.displayName ?? (isResolvedAction ? t("stipulations.complete") : undefined);

  const persistAction = (actionCode: string, reasonPk?: number, noteText?: string) => {
    setSaveError(undefined);

    if (!approvalId) {
      return;
    }
    if (stip.stipulationAppliedId === undefined) {
      setSaveError(t("stipulations.saveError", { message: t("stipulations.saveMissingRowId") }));
      return;
    }

    applyAction.mutate(
      {
        stipulationId: stip.id,
        stipCode: stip.stipCode,
        stipulationAppliedId: stip.stipulationAppliedId,
        actionCode,
        reasonPk,
        noteText,
      },
      {
        onSuccess: () => {
          clearEdit(stip.id);
        },
        onError: (error) => {
          setSaveError(t("stipulations.saveError", { message: getErrorMessage(error) }));
        },
      }
    );
  };

  const handleSelect = (action: StipulationActionView, reason?: StipulationActionReasonView) => {
    setAction(stip.id, action.actionCode, reason?.reasonPk);

    if (action.requiresNote) {
      return;
    }

    persistAction(action.actionCode, reason?.reasonPk);
  };

  const handlePorExceptionChange = (checked: boolean) => {
    const previousChecked = edit.porExceptionChecked ?? stip.porExceptionInd ?? false;
    setPorException(stip.id, checked);
    setPorError(undefined);

    if (!approvalId || !stip.applicantPk) {
      return;
    }

    porExceptionMutation.mutate(
      {
        approvalId,
        applicantPk: stip.applicantPk,
        checked,
      },
      {
        onSuccess: () => {
          clearEdit(stip.id);
        },
        onError: (error) => {
          setPorError(t("stipulations.saveError", { message: getErrorMessage(error) }));
          setPorException(stip.id, previousChecked);
        },
      }
    );
  };

  return {
    selectedAction,
    selectedReason,
    isResolvedAction,
    currentLabel,
    note: edit.note,
    porExceptionChecked: edit.porExceptionChecked ?? stip.porExceptionInd ?? false,
    saveError,
    porError,
    isSaving: applyAction.isPending,
    isPorSaving: porExceptionMutation.isPending,
    handleSelect,
    persistAction,
    setNote: (note: string) => setNoteInStore(stip.id, note),
    handlePorExceptionChange,
  };
}
